import React, { useState, useEffect } from "react";
import {
    Typography,
    DatePicker,
    Spin,
    Layout,
    Form,
    Button,
    Space,
    Select,
} from "antd";
import { ProfileOutlined, FileExcelOutlined } from "@ant-design/icons";
import moment from "moment";
import { Table } from "ant-table-extensions";
import { errorN } from "../../general";
import { navigate } from "hookrouter";
import useFetch from "use-http";

const { Content } = Layout;
const { RangePicker } = DatePicker;
const { Text } = Typography;
const { Option } = Select;

const formatter = new Intl.NumberFormat("en-MY", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
});

// Same as Windows frmList_DailyRevenue: units sold and revenue per room item, per day.
function DailyRevenue() {
    const { get, response, loading } = useFetch();

    const [form] = Form.useForm();
    const [data, setData] = useState([]);
    const [days, setDays] = useState([]);
    const [range, setRange] = useState("");

    async function loadData() {
        try {
            const values = await form.validateFields();

            const start = moment(values.dtDateRange[0]).startOf("day");
            const end = moment(values.dtDateRange[1]).startOf("day");

            if (end.diff(start, "days") > 31) {
                errorN("Please choose a date range of 31 days or less.");
                return;
            }

            setRange(`From ${start.format("DD-MM-YYYY")} to ${end.format("DD-MM-YYYY")}`);

            const property = JSON.parse(localStorage.getItem("currentproperty"));
            const s = start.clone().utc().format("YYYY-MM-DDTHH:mm:ss[Z]");
            const e = end.clone().utc().format("YYYY-MM-DDTHH:mm:ss[Z]");

            const itemList = await get(`/api/hotels/${property.id}/items`);
            if (!response.ok) {
                if (response.status === 401) {
                    localStorage.removeItem("session");
                    window.location.reload();
                }
                errorN(`Get Items - ${response.status}: ${response.statusText}. Please contact the administrator.`);
                return;
            }

            const revenueList = await get(`/api/hotels/${property.id}/revenue?start=${s}&end=${e}`);
            if (!response.ok) {
                if (response.status === 401) {
                    localStorage.removeItem("session");
                    window.location.reload();
                }
                errorN(`Get Revenue - ${response.status}: ${response.statusText}. Please contact the administrator.`);
                return;
            }

            let rows = revenueList || [];
            if (values.type === "Invoiced") {
                rows = rows.filter(x => x.folio && x.folio.status === "Invoiced");
            }

            const dayList = [];
            for (let d = start.clone(); d.isSameOrBefore(end); d.add(1, "days")) {
                dayList.push(d.format("DDMMYYYY"));
            }

            const items = (itemList || []).filter(
                x => x.data && x.data.type && x.data.type.includes("reservation")
            );

            const result = items.map(it => {
                const row = { key: it.id, itemID: it.id, itemName: it.data.name, totalUnit: 0, totalRevenue: 0 };
                for (const d of dayList) {
                    let unit = 0;
                    let price = 0;
                    for (const r of rows) {
                        if (r.item === it.id && moment(r.date).format("DDMMYYYY") === d) {
                            unit = unit + (r.unit || 0);
                            price = price + parseFloat((r.rate && r.rate.price) || 0);
                        }
                    }
                    row[`dt${d}`] = unit;
                    row[`price${d}`] = price;
                    row.totalUnit = row.totalUnit + unit;
                    row.totalRevenue = row.totalRevenue + price;
                }
                return row;
            });

            setDays(dayList);
            setData(result);
        } catch (err) {
            errorN(`Error Load Data: ${err}`);
        }
    }

    useEffect(() => {
        if (!localStorage.getItem("session")) {
            navigate("/");
        }
        form.setFieldsValue({
            dtDateRange: [moment().startOf("day"), moment().startOf("day").add(1, "days")],
            type: "All",
        });
    }, []);

    const dayColumns = [];
    for (const d of days) {
        const label = moment(d, "DDMMYYYY").format("DD-MM");
        dayColumns.push({
            width: 110,
            title: `Unit Sold (${label})`,
            dataIndex: `dt${d}`,
            key: `dt${d}`,
            align: "right",
            formatter: v => v ?? 0,
        });
        dayColumns.push({
            width: 120,
            title: `Revenue (${label})`,
            dataIndex: `price${d}`,
            key: `price${d}`,
            align: "right",
            render: v => formatter.format(v || 0),
            formatter: v => formatter.format(v || 0),
        });
    }

    const columns = [
        {
            width: 70,
            title: "ID",
            dataIndex: "itemID",
            key: "itemID",
            fixed: "left",
            formatter: v => v,
        },
        {
            width: 180,
            title: "Name",
            dataIndex: "itemName",
            key: "itemName",
            fixed: "left",
            formatter: v => v,
        },
        ...dayColumns,
        {
            width: 100,
            title: "Total Unit",
            dataIndex: "totalUnit",
            key: "totalUnit",
            align: "right",
            formatter: v => v ?? 0,
        },
        {
            width: 130,
            title: "Total Revenue",
            dataIndex: "totalRevenue",
            key: "totalRevenue",
            align: "right",
            render: v => formatter.format(v || 0),
            formatter: v => formatter.format(v || 0),
        },
    ];

    return (
        <>
            <div
                style={{
                    backgroundImage: `url("/header-bg7.png")`,
                    padding: "15px",
                    fontSize: "18px",
                }}
            >
                <ProfileOutlined /> &nbsp; Daily Revenue
            </div>

            <Layout>
                <Spin spinning={loading}>
                    <Content style={{ backgroundColor: "white" }}>
                        <Form form={form} layout="vertical">
                            <div style={{ padding: 20, height: "73vh", overflow: "auto" }}>
                                <Space>
                                    <Form.Item
                                        label="Start - To"
                                        name="dtDateRange"
                                        rules={[{ required: true, message: "Dates missing!" }]}
                                    >
                                        <RangePicker format="DD-MM-YYYY" />
                                    </Form.Item>

                                    <Form.Item label="Type" name="type">
                                        <Select style={{ width: 120 }}>
                                            <Option value="All">All</Option>
                                            <Option value="Invoiced">Invoiced</Option>
                                        </Select>
                                    </Form.Item>

                                    <Button type="primary" onClick={loadData}>
                                        Load
                                    </Button>
                                </Space>

                                <div><Text type="secondary">{range}</Text></div>

                                <Table
                                    columns={columns}
                                    dataSource={data}
                                    exportable
                                    exportableProps={{
                                        showColumnPicker: true,
                                        fileName: "DailyRevenue-List",
                                        btnProps: {
                                            type: "primary",
                                            icon: <FileExcelOutlined />,
                                        },
                                    }}
                                    bordered
                                    size="small"
                                    scroll={{ x: "max-content", y: 360 }}
                                    pagination={false}
                                    footer={pageData => {
                                        const units = pageData.reduce((sum, r) => sum + r.totalUnit, 0);
                                        const revenue = pageData.reduce((sum, r) => sum + r.totalRevenue, 0);
                                        return (
                                            <Text strong>
                                                Total Unit Sold: {units} &nbsp;&nbsp; Total Revenue: {formatter.format(revenue)}
                                            </Text>
                                        );
                                    }}
                                />
                            </div>
                        </Form>
                    </Content>
                </Spin>
            </Layout>
        </>
    );
}

export default DailyRevenue;
